import { topicLabel } from '@gogrov/shared'
import { ROUTES } from '@/components/gg/chrome'
import type { FeedHeadline } from './feedTypes'

/**
 * The feed's words and small rules (Brian's headlines.html v7.1, desk.html
 * v1.5). Wording is his; numbers are his: kept 7 days, a week is "fresh".
 */

export const KEEP_DAYS = 7
const DAY = 864e5

export const dayKey = (t: string | number | Date) => { const d = new Date(t); d.setHours(0, 0, 0, 0); return d.getTime() }
const today = () => dayKey(Date.now())

/** "Today", "Yesterday", or the weekday. */
export function dayName(t: string): string {
  const d = Math.round((today() - dayKey(t)) / DAY)
  return d <= 0 ? 'Today' : d === 1 ? 'Yesterday' : new Date(t).toLocaleDateString('en-US', { weekday: 'long' })
}

/** For an older day's header: how long its stories stay. */
export function leftOn(t: string): string {
  const d = KEEP_DAYS - Math.round((today() - dayKey(t)) / DAY)
  return d <= 1 ? 'Leaves tomorrow' : `${d} days left`
}

/** When it was reported: the outlet's date when we have it, else when it was dealt. */
export const reportedAt = (h: FeedHeadline) => h.source_published_at || h.fetched_at

/** Days old. */
export const daysOld = (h: FeedHeadline) => Math.max(0, Math.floor((Date.now() - Date.parse(reportedAt(h))) / DAY))

/** Older than a week: check it's still current (Brian v4.8). */
export const isStale = (h: FeedHeadline) => !isRecord(h) && daysOld(h) > KEEP_DAYS

/** "38m", "4h", "2d". */
export function ageOf(h: FeedHeadline): string {
  const ms = Math.max(0, Date.now() - Date.parse(reportedAt(h)))
  const m = Math.round(ms / 60000)
  if (m < 60) return `${Math.max(1, m)}m`
  const hr = Math.round(m / 60)
  if (hr < 24) return `${hr}h`
  return `${Math.round(hr / 24)}d`
}

export const isRecord = (h: FeedHeadline) => h.provenance === 'record'

/** "4 sources", or "Reported" (Brian v4.8: we are not a news outlet). */
export function sourcesLabel(h: FeedHeadline): string {
  const n = h.source_count ?? 0
  return n > 1 ? `${n} sources` : 'Reported'
}

export const labelOf = (h: FeedHeadline) => topicLabel(h.topic) || 'Story'

/** The keyword that put it at the top, if any. */
export const hitOf = (h: FeedHeadline): string | null => (h.keyword_hits && h.keyword_hits[0]) || null

export type Cause = 'record' | 'keyword' | 'mix'
export const causeOf = (h: FeedHeadline): Cause => (isRecord(h) ? 'record' : hitOf(h) ? 'keyword' : 'mix')

/** The control that brought a story, and where it lives. */
export function controlOf(h: FeedHeadline): { to: string; label: string } {
  const c = causeOf(h)
  if (c === 'record') return { to: ROUTES.recordsSettings, label: 'Public records' }
  if (c === 'keyword') return { to: ROUTES.topics, label: 'Topics and keywords' }
  return { to: ROUTES.topics, label: 'Topics' }
}

export const plural = (n: number, one: string, many = one + 's') => `${n} ${n === 1 ? one : many}`

/** The house rule: no em dashes in anything a person reads. */
export const noDash = (s: string | null | undefined) => (s ?? '').replace(/\s+[—–]\s+/g, ': ').replace(/—/g, ', ')

/**
 * "Breaking as of 6:41 this morning", or "Breaking as of Tuesday, 9:12" for
 * an earlier day (Brian's headlines.html v2.5, spec §10.4.2).
 */
export function asOf(t: string): string {
  const d = new Date(t), hr = d.getHours()
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).replace(/ ?(AM|PM)$/i, '')
  if (dayKey(d) < today()) return `Breaking as of ${d.toLocaleDateString('en-US', { weekday: 'long' })}, ${time}`
  return `Breaking as of ${time} ${hr < 12 ? 'this morning' : hr < 17 ? 'this afternoon' : 'tonight'}`
}

/** A server's own words when it gave some; never a transport error's ("Edge Function returned a non-2xx status code"). */
export function plainError(e: unknown, fallback: string): string {
  const m = (e as { message?: string } | null)?.message ?? ''
  return !m || /non-2xx|edge function|failed to fetch|fetch failed|networkerror|load failed|relay error/i.test(m) ? fallback : m
}
